import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { RECALL_GOAL_LABEL } from '../../settings-recall-agent/types';
import type { BuilderStepId, RecallDraft } from '../../recall-builder/types';
import { contactableNow } from '../../recall-builder/derive';

const STEPS: { id: BuilderStepId; n: number; label: string }[] = [
  { id: 'audience', n: 1, label: 'Audience' },
  { id: 'messages', n: 2, label: 'Messages' },
  { id: 'test', n: 3, label: 'Test and start' },
];

export interface BuilderHeaderProps {
  active: BuilderStepId;
  titleLead: string;
  titleAccent: string;
  /** Punctuation after the accent word, so a question mark is not coloured. */
  titleTail?: string;
  sub: string;
  /** Completed steps are links back (§1). */
  onGoTo?: (step: BuilderStepId) => void;
  /** Which steps may be navigated to — a step behind the current one always may. */
  reachable?: BuilderStepId[];
}

/** The stepper, shown at the top of all three pages. */
export function BuilderHeader({
  active,
  titleLead,
  titleAccent,
  titleTail = '',
  sub,
  onGoTo,
  reachable,
}: BuilderHeaderProps) {
  const activeIndex = STEPS.findIndex((s) => s.id === active);
  return (
    <div>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
        Recall › New recall
      </div>
      <h1 className="m-0 mt-1 text-[26px] font-bold leading-[1.15] tracking-[-0.025em] text-[var(--text-primary)]">
        {titleLead} <span className="text-[var(--brand-indigo)]">{titleAccent}</span>
        {titleTail}
      </h1>
      <p className="m-0 mt-1.5 max-w-[680px] text-[13px] leading-[1.6] text-[var(--text-muted)]">{sub}</p>

      <div className="mt-4 flex items-center gap-2.5">
        {STEPS.map((s, i) => {
          const done = i < activeIndex;
          const on = s.id === active;
          const canGo = done || (reachable?.includes(s.id) ?? false);
          return (
            <React.Fragment key={s.id}>
              <button
                type="button"
                disabled={!canGo || on}
                onClick={() => { onGoTo?.(s.id); }}
                className={cn(
                  'inline-flex items-center gap-1.5',
                  canGo && !on ? 'cursor-pointer hover:underline' : 'cursor-default',
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-[22px] w-[22px] items-center justify-center rounded-full font-[var(--font-mono)] text-[12.5px] font-bold',
                    on || done
                      ? 'bg-[var(--brand-indigo)] text-white'
                      : 'bg-[#F1F3F9] text-[var(--text-muted)]',
                  )}
                >
                  {done ? <Icon kind="check" size={12} stroke={3} /> : s.n}
                </span>
                <span
                  className={cn(
                    'text-[12.5px]',
                    on ? 'font-bold text-[var(--text-primary)]' : 'text-[var(--text-muted)]',
                  )}
                >
                  {s.label}
                </span>
              </button>
              {i < STEPS.length - 1 ? <span className="h-px w-8 bg-[var(--border-default)]" /> : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The top bar on pages 2 and 3 — **the recall is named once you know what is in
 * it**, which is why the name lives here rather than on page 1.
 */
export function BuilderTopBar({
  draft,
  onName,
  onChangeGoal,
  onChangeAudience,
}: {
  draft: RecallDraft;
  onName: (next: string) => void;
  onChangeGoal: () => void;
  onChangeAudience: () => void;
}) {
  return (
    <div className="mt-5 flex flex-wrap items-end gap-6 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] px-5 py-4">
      <div className="min-w-[260px] flex-1">
        <label htmlFor="rb-name" className="block text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
          Name
        </label>
        <input
          id="rb-name"
          value={draft.name}
          placeholder="Untitled recall"
          onChange={(e) => { onName(e.target.value); }}
          className="mt-1 w-full border-0 border-b border-[var(--border-default)] bg-transparent pb-1 text-[19px] font-bold tracking-[-0.02em] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
        />
        <div className="mt-1 text-[11.5px] text-[var(--text-muted)]">
          Staff see this name on the recall list and on its report.
        </div>
      </div>

      <div className="shrink-0">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Goal</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-[14px] font-semibold text-[var(--text-primary)]">
            {draft.goal === undefined ? 'Not set' : RECALL_GOAL_LABEL[draft.goal]}
          </span>
          <button type="button" onClick={onChangeGoal} className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
            Change →
          </button>
        </div>
      </div>

      <div className="shrink-0">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Who</div>
        <div className="mt-1 flex items-baseline gap-2">
          {/* Less anyone left out on page 1 — this read the raw count before, so
              it never moved when somebody was excluded (spec 23 §1.3). */}
          <span className="font-[var(--font-mono)] text-[14px] font-bold text-[var(--text-primary)]">
            {contactableNow(draft).toLocaleString('en-US')}
          </span>
          <span className="text-[12.5px] text-[var(--text-secondary)]">patients</span>
          <button type="button" onClick={onChangeAudience} className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
            Change →
          </button>
        </div>
      </div>
    </div>
  );
}

/** The page shell — every builder page owns its own scroll (the house rule). */
export function BuilderPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1500px] flex-1 overflow-y-auto px-8 pb-16 pt-6">
      {children}
    </div>
  );
}

/** The footer every page carries — the draft promise, then the actions. */
export function BuilderFooter({
  note,
  left,
  children,
}: {
  note?: React.ReactNode;
  left?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{left ?? note}</div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

export function BuilderButton({
  kind = 'ghost',
  disabled,
  onClick,
  title,
  children,
}: {
  kind?: 'primary' | 'ghost' | 'soft';
  disabled?: boolean;
  onClick?: () => void;
  title?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={cn(
        'rounded-[10px] px-4 py-2 text-[13.5px] font-semibold',
        kind === 'primary' && !disabled && 'bg-[var(--brand-indigo)] text-white',
        kind === 'primary' && disabled === true && 'cursor-not-allowed bg-[#B9BFD4] text-white',
        kind === 'soft' && 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]',
        kind === 'ghost' &&
          'border border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The fix list (§3.7). **Not a disabled button with no explanation** — each issue
 * is a link to the step that carries it.
 */
export function FixList({
  issues,
  onGoToStep,
}: {
  issues: { stepId: string; stepIndex: number; long: string }[];
  onGoToStep?: (stepId: string) => void;
}) {
  if (issues.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--money-green)]">
        <Icon kind="check" size={13} stroke={3} />
        Every step is ready.
      </span>
    );
  }
  return (
    <span className="text-[12.5px] text-[var(--text-secondary)]">
      <b className="font-semibold text-[var(--alert-red)]">
        {issues.length} thing{issues.length === 1 ? '' : 's'} to fix before you can start:
      </b>{' '}
      {issues.map((it, i) => (
        <React.Fragment key={`${it.stepId}-${String(i)}`}>
          {i > 0 ? ' · ' : ' '}
          <button
            type="button"
            onClick={() => { onGoToStep?.(it.stepId); }}
            className="font-semibold text-[var(--brand-indigo)] hover:underline"
          >
            {it.long}
          </button>
        </React.Fragment>
      ))}
    </span>
  );
}
